import type {ComputedRef, Ref} from 'vue'
import {computed, ref} from 'vue'
import type {DataColumn, DataProvider, DataRow} from '@hantera/design-system'
import type audit from '@/backend/audit'
import {AuditLogEntry} from '@/backend/audit'

export type { AuditLogEntry, AuditFieldChange } from '@/backend/audit'

export interface AuditFilters {
  pathPrefix: string
  timespan: {
    from: Date
    to: Date
  }
  field: string
}

export class AuditDataProvider implements DataProvider {
  private filters: Ref<AuditFilters>
  private auditService: ReturnType<typeof audit>
  private _rows = ref<DataRow[]>([])
  private _isLoading = ref(false)
  private _allLoaded = ref(false)
  private _nextCursor: string | null = null
  private _entries: import('@/backend/audit').AuditLogEntry[] = []

  public rows: ComputedRef<DataRow[]>
  public isLoading: ComputedRef<boolean>
  public allLoaded: ComputedRef<boolean>
  public columns: ComputedRef<DataColumn[]>
  public totalCount: ComputedRef<{
    count: number;
    cap?: number;
  }>

  constructor(filters: Ref<AuditFilters>, auditService: ReturnType<typeof audit>) {
    this.filters = filters
    this.auditService = auditService

    this.rows = computed(() => this._rows.value)
    this.isLoading = computed(() => this._isLoading.value)
    this.allLoaded = computed(() => this._allLoaded.value)
    this.totalCount = computed(() => ({
      count: this._rows.value.length,
      cap: this._rows.value.length // Audit long length is unknown, so always display "+"
    }))

    this.columns = computed(() => [
      {
        key: 'timestamp',
        valueIdx: 0,
        label: 'Time',
        canSort: false,
        canFilter: false,
        size: 180
      },
      {
        key: 'action',
        valueIdx: 1,
        label: 'Action',
        canSort: false,
        canFilter: false,
        size: 100
      },
      {
        key: 'resourcePath',
        valueIdx: 2,
        label: 'Resource',
        canSort: false,
        canFilter: false
      }
    ])
  }

  async refresh(): Promise<void> {
    this._isLoading.value = true
    this._nextCursor = null
    this._entries = []
    this._rows.value = []

    try {
      const result = await this.auditService.query({
        pathPrefix: this.filters.value.pathPrefix || undefined,
        from: this.filters.value.timespan.from?.toISOString(),
        to: this.filters.value.timespan.to?.toISOString(),
        field: this.filters.value.field || undefined,
        limit: 50
      })

      this._entries = result.entries
      this._nextCursor = result.nextCursor || null
      this._allLoaded.value = !result.hasMore

      this._rows.value = this._entries.map((entry) => this.entryToRow(entry))
    } catch (error) {
      console.error('Failed to fetch audit logs:', error)
      this._allLoaded.value = true
    } finally {
      this._isLoading.value = false
    }
  }

  async loadMore(): Promise<void> {
    if (this._allLoaded.value || !this._nextCursor || this._isLoading.value) {
      return
    }

    this._isLoading.value = true

    try {
      const result = await this.auditService.query({
        pathPrefix: this.filters.value.pathPrefix || undefined,
        from: this.filters.value.timespan.from?.toISOString(),
        to: this.filters.value.timespan.to?.toISOString(),
        field: this.filters.value.field || undefined,
        after: this._nextCursor,
        limit: 50
      })

      this._entries.push(...result.entries)
      this._nextCursor = result.nextCursor || null
      this._allLoaded.value = !result.hasMore

      this._rows.value = this._entries.map((entry) => this.entryToRow(entry))
    } catch (error) {
      console.error('Failed to load more audit logs:', error)
      this._allLoaded.value = true
    } finally {
      this._isLoading.value = false
    }
  }

  private entryToRow(entry: AuditLogEntry): DataRow {
    const date = new Date(entry.timestamp)
    const formattedTime = date.toLocaleString()

    return {
      key: entry.auditLogEntryId,
      values: [
        formattedTime,
        entry.action,
        entry.resourcePath.join('/')
      ]
    }
  }

  getEntry(rowKey: string): AuditLogEntry | undefined {
    return this._entries.find((e) => e.auditLogEntryId === rowKey)
  }
}
